/**
 * Data Management Service - API Integration
 * ==========================================
 * Service for managing custom data tables via backend API
 * Handles CRUD operations, CSV import/export, and schema management
 * 
 * Backend API Base: http://77.42.81.171:5000/api (production) or http://localhost:5000/api (local)
 * Documentation: DATA_MANAGEMENT_API.md
 */

import Papa from 'papaparse';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://77.42.81.171:5000/api (production) or http://localhost:5000/api (local)';

// ============================================
// HELPER: API REQUEST
// ============================================

const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    };
    
    // Remove Content-Type for FormData
    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    console.log(`ðŸŒ [DataManagement] ${options.method || 'GET'} ${url}`);
    
    const response = await fetch(url, config);
    
    // Log response status
    console.log(`ðŸ“¡ [DataManagement] Response status: ${response.status}`);
    
    const data = await response.json();
    
    // Log the actual response
    console.log(`ðŸ“¦ [DataManagement] Response data:`, data);
    
    if (!response.ok || data?.success === false) {
      console.error(`âŒ [DataManagement] API Error:`, data);
      throw new Error(data?.message || data?.error || `HTTP ${response.status}`);
    }
    
    console.log(`âœ… [DataManagement] API Success:`, endpoint);
    
    // Return data?.data first, then fall back to data
    return data?.data !== undefined ? data.data : data;
  } catch (error) {
    console.error(`âŒ [DataManagement] Request failed for ${endpoint}:`, error);
    throw error;
  }
};

// ============================================
// SCHEMA OPERATIONS
// ============================================

/**
 * Create a new data table schema
 * @param {string} appPrefix - App identifier (e.g., 'myapp')
 * @param {string} tableName - Table name (e.g., 'users')
 * @param {Array} fields - Array of field definitions [{name, type, required, options}]
 * @param {string} userId - Current user ID
 */
export const createTableSchema = async (appPrefix, tableName, fields, userId) => {
  try {
    console.log(`ðŸ“Š [DataManagement] Creating table schema: ${tableName}`);
    
    // Format fields with options array
    const formattedFields = fields.map(field => ({
      name: field.name,
      type: field.type || 'text',
      required: field.required || false,
      options: field.options || []
    }));
    
    const result = await apiRequest('/data-schemas/create', {
      method: 'POST',
      body: JSON.stringify({
        appId: appPrefix,
        appPrefix: appPrefix,
        displayName: tableName,
        userId: userId,
        fields: formattedFields
      })
    });
    
    console.log(`âœ… [DataManagement] Schema created:`, result);
    return { 
      success: true, 
      schemaId: result.schemaId,
      tableName: result.displayName 
    };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to create schema:', error);
    throw error;
  }
};

/**
 * Get all table schemas for an app
 * @param {string} appPrefix - App identifier
 */
export const getTableSchemas = async (appPrefix) => {
  try {
    console.log(`ðŸ“Š [DataManagement] ========== GET SCHEMAS START ==========`);
    console.log(`ðŸ“Š [DataManagement] Fetching schemas for appPrefix: "${appPrefix}"`);
    console.log(`ðŸ“Š [DataManagement] Type of appPrefix: ${typeof appPrefix}`);
    
    const data = await apiRequest(`/data-schemas/${appPrefix}?appPrefix=${appPrefix}`);
    
    console.log(`ðŸ“Š [DataManagement] ========== RAW API RESPONSE ==========`);
    console.log(`ðŸ“Š [DataManagement] Type of response:`, typeof data);
    console.log(`ðŸ“Š [DataManagement] Is array:`, Array.isArray(data));
    console.log(`ðŸ“Š [DataManagement] Response keys:`, data ? Object.keys(data) : 'null');
    console.log(`ðŸ“Š [DataManagement] Full response:`, JSON.stringify(data, null, 2));
    
    // Handle different response structures with extensive logging
    let schemas = [];
    
    if (Array.isArray(data)) {
      console.log(`ðŸ“Š [DataManagement] âœ“ Response is direct array, length: ${data.length}`);
      schemas = data;
    } else if (data && typeof data === 'object') {
      console.log(`ðŸ“Š [DataManagement] Response is object, checking nested fields...`);
      
      // Try all possible locations
      if (data.schemas) {
        console.log(`ðŸ“Š [DataManagement] âœ“ Found in data.schemas, length: ${data.schemas.length}`);
        schemas = data.schemas;
      } else if (data.data) {
        console.log(`ðŸ“Š [DataManagement] âœ“ Found in data.data, length: ${data.data?.length || 0}`);
        schemas = Array.isArray(data.data) ? data.data : [];
      } else if (data.results) {
        console.log(`ðŸ“Š [DataManagement] âœ“ Found in data.results, length: ${data.results.length}`);
        schemas = data.results;
      } else {
        console.log(`ðŸ“Š [DataManagement] âš ï¸ No known schema array found in response`);
        console.log(`ðŸ“Š [DataManagement] Available keys:`, Object.keys(data));
      }
    } else {
      console.log(`ðŸ“Š [DataManagement] âŒ Response is neither array nor object`);
    }
    
    console.log(`ðŸ“Š [DataManagement] ========== EXTRACTED SCHEMAS ==========`);
    console.log(`ðŸ“Š [DataManagement] Schemas type:`, typeof schemas);
    console.log(`ðŸ“Š [DataManagement] Schemas is array:`, Array.isArray(schemas));
    console.log(`ðŸ“Š [DataManagement] Schemas length:`, schemas?.length);
    console.log(`ðŸ“Š [DataManagement] Schemas content:`, JSON.stringify(schemas, null, 2));
    
    // Map API response to component format with validation
    const mappedSchemas = schemas
      .filter(schema => {
        const isValid = schema && (schema.schemaId || schema.id);
        if (!isValid) {
          console.warn(`ðŸ“Š [DataManagement] âš ï¸ Skipping invalid schema:`, schema);
        }
        return isValid;
      })
      .map((schema, index) => {
        console.log(`ðŸ“Š [DataManagement] Mapping schema ${index}:`, schema);
        
        const mapped = {
          id: schema.schemaId || schema.id,
          tableName: schema.displayName || schema.tableName || 'Unnamed Table',
          appPrefix: schema.appPrefix || appPrefix,
          fields: schema.fields || [],
          createdBy: schema.createdBy,
          createdAt: schema.createdAt,
          updatedAt: schema.updatedAt,
          recordCount: schema.recordCount || 0
        };
        
        console.log(`ðŸ“Š [DataManagement] Mapped to:`, mapped);
        return mapped;
      });
    
    console.log(`ðŸ“Š [DataManagement] ========== FINAL RESULT ==========`);
    console.log(`âœ… [DataManagement] Returning ${mappedSchemas.length} mapped schemas`);
    console.log(`ðŸ“Š [DataManagement] Final schemas:`, JSON.stringify(mappedSchemas, null, 2));
    
    return mappedSchemas;
  } catch (error) {
    console.error('âŒ [DataManagement] ========== ERROR ==========');
    console.error('âŒ [DataManagement] Failed to fetch schemas:', error);
    console.error('âŒ [DataManagement] Error message:', error.message);
    console.error('âŒ [DataManagement] Error stack:', error.stack);
    return [];
  }
};

/**
 * Get a specific table schema
 * @param {string} appPrefix - App identifier
 * @param {string} schemaId - Schema ID
 */
export const getTableSchema = async (appPrefix, schemaId) => {
  try {
    console.log(`ðŸ“Š [DataManagement] Fetching schema: ${schemaId}`);
    
    const data = await apiRequest(`/data-schemas/${appPrefix}/${schemaId}?appPrefix=${appPrefix}`);
    
    return {
      id: data.schemaId,
      tableName: data.displayName,
      appPrefix: data.appPrefix,
      fields: data.fields || [],
      createdBy: data.createdBy,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      recordCount: data.recordCount || 0
    };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to fetch schema:', error);
    return null;
  }
};

/**
 * Delete a table schema and all its data
 * @param {string} appPrefix - App identifier
 * @param {string} tableName - Table name (not used, kept for compatibility)
 * @param {string} schemaId - Schema document ID
 * @param {string} userId - Current user ID
 */
export const deleteTableSchema = async (appPrefix, tableName, schemaId, userId) => {
  try {
    console.log(`ðŸ—‘ï¸ [DataManagement] Deleting table schema: ${schemaId}`);
    
    await apiRequest(`/data-schemas/${schemaId}`, {
      method: 'DELETE',
      body: JSON.stringify({
        appId: appPrefix,
        appPrefix: appPrefix,
        userId: userId,
        confirmDelete: true
      })
    });
    
    console.log(`âœ… [DataManagement] Table deleted: ${schemaId}`);
    return { success: true };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to delete table:', error);
    throw error;
  }
};

// ============================================
// RECORD OPERATIONS (CRUD)
// ============================================

/**
 * Create a new record in a data table
 * @param {string} appPrefix - App identifier
 * @param {string} tableName - Table name (displayName)
 * @param {Object} data - Record data
 * @param {string} userId - Current user ID
 */
export const createRecord = async (appPrefix, tableName, data, userId, schemaId) => {
  try {
    console.log(`ðŸ“ [DataManagement] Creating record in: ${tableName}`);
    
    // If schemaId not provided, fetch it
    let finalSchemaId = schemaId;
    if (!finalSchemaId) {
      const schemas = await getTableSchemas(appPrefix);
      const schema = schemas.find(s => s.tableName === tableName);
      if (!schema) {
        throw new Error(`Schema not found for table: ${tableName}`);
      }
      finalSchemaId = schema.id;
    }
    
    const result = await apiRequest('/data-records/create', {
      method: 'POST',
      body: JSON.stringify({
        appId: appPrefix,
        appPrefix: appPrefix,
        schemaId: finalSchemaId,
        userId: userId,
        data: data
      })
    });
    
    console.log(`âœ… [DataManagement] Record created with ID: ${result.id}`);
    return { success: true, id: result.id, data: result };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to create record:', error);
    throw error;
  }
};

/**
 * Get all records from a data table with pagination
 * @param {string} appPrefix - App identifier
 * @param {string} tableName - Table name (displayName)
 * @param {Object} options - Query options {pageSize, page, sortBy, sortOrder, searchQuery}
 */
export const getRecords = async (appPrefix, tableName, options = {}) => {
  try {
    console.log(`ðŸ“Š [DataManagement] Fetching records from: ${tableName}`);
    
    // Get schema to get schemaId
    const schemas = await getTableSchemas(appPrefix);
    const schema = schemas.find(s => s.tableName === tableName);
    if (!schema) {
      throw new Error(`Schema not found for table: ${tableName}`);
    }
    
    const { 
      pageSize = 20, 
      page = 1,
      sortBy = 'createdAt',
      sortOrder = 'desc',
      searchQuery = ''
    } = options;
    
    const params = new URLSearchParams({
      appId: appPrefix,
      appPrefix: appPrefix,
      schemaId: schema.id,
      page: page.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      sortOrder
    });
    
    if (searchQuery) {
      params.append('searchQuery', searchQuery);
    }
    
    const data = await apiRequest(`/data-records/list?${params.toString()}`);
    
    const records = Array.isArray(data) ? data : (data?.records || []);
    
    console.log(`âœ… [DataManagement] Fetched ${records.length} records`);
    
    return {
      records,
      hasMore: data?.hasMore || false,
      total: data?.total || records.length
    };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to fetch records:', error);
    return { records: [], hasMore: false, total: 0 };
  }
};

/**
 * Get total count of records in a table
 * @param {string} appPrefix - App identifier
 * @param {string} tableName - Table name
 */
export const getRecordCount = async (appPrefix, tableName) => {
  try {
    const result = await getRecords(appPrefix, tableName, { pageSize: 1 });
    return result.total || 0;
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to count records:', error);
    return 0;
  }
};

/**
 * Update a record in a data table
 * @param {string} appPrefix - App identifier
 * @param {string} tableName - Table name
 * @param {string} recordId - Record ID
 * @param {Object} data - Updated data
 * @param {string} userId - Current user ID
 */
export const updateRecord = async (appPrefix, tableName, recordId, data, userId) => {
  try {
    console.log(`âœï¸ [DataManagement] Updating record: ${recordId}`);
    
    // Get schema to get schemaId
    const schemas = await getTableSchemas(appPrefix);
    const schema = schemas.find(s => s.tableName === tableName);
    if (!schema) {
      throw new Error(`Schema not found for table: ${tableName}`);
    }
    
    const result = await apiRequest(`/data-records/update/${recordId}`, {
      method: 'PUT',
      body: JSON.stringify({
        appId: appPrefix,
        appPrefix: appPrefix,
        schemaId: schema.id,
        userId: userId,
        data: data
      })
    });
    
    console.log(`âœ… [DataManagement] Record updated: ${recordId}`);
    return { success: true, data: result };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to update record:', error);
    throw error;
  }
};

/**
 * Delete a record from a data table
 * @param {string} appPrefix - App identifier
 * @param {string} tableName - Table name
 * @param {string} recordId - Record ID
 */
export const deleteRecord = async (appPrefix, tableName, recordId) => {
  try {
    console.log(`ðŸ—‘ï¸ [DataManagement] Deleting record: ${recordId}`);
    
    // Get schema to get schemaId
    const schemas = await getTableSchemas(appPrefix);
    const schema = schemas.find(s => s.tableName === tableName);
    if (!schema) {
      throw new Error(`Schema not found for table: ${tableName}`);
    }
    
    await apiRequest(`/data-records/delete/${recordId}`, {
      method: 'DELETE',
      body: JSON.stringify({
        appId: appPrefix,
        appPrefix: appPrefix,
        schemaId: schema.id
      })
    });
    
    console.log(`âœ… [DataManagement] Record deleted: ${recordId}`);
    return { success: true };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to delete record:', error);
    throw error;
  }
};

/**
 * Bulk delete records
 * @param {string} appPrefix - App identifier
 * @param {string} tableName - Table name
 * @param {Array} recordIds - Array of record IDs to delete
 */
export const bulkDeleteRecords = async (appPrefix, tableName, recordIds) => {
  try {
    console.log(`ðŸ—‘ï¸ [DataManagement] Bulk deleting ${recordIds.length} records`);
    
    // Get schema to get schemaId
    const schemas = await getTableSchemas(appPrefix);
    const schema = schemas.find(s => s.tableName === tableName);
    if (!schema) {
      throw new Error(`Schema not found for table: ${tableName}`);
    }
    
    const result = await apiRequest('/data-records/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({
        appId: appPrefix,
        appPrefix: appPrefix,
        schemaId: schema.id,
        recordIds: recordIds
      })
    });
    
    console.log(`âœ… [DataManagement] Bulk delete completed: ${recordIds.length} records`);
    return { success: true, count: result.deletedCount || recordIds.length };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to bulk delete records:', error);
    throw error;
  }
};

/**
 * Bulk create records
 * @param {string} appPrefix - App identifier
 * @param {string} tableName - Table name
 * @param {Array} records - Array of records to create
 * @param {string} userId - Current user ID
 */
export const bulkCreateRecords = async (appPrefix, tableName, records, userId) => {
  try {
    console.log(`ðŸ“ [DataManagement] Bulk creating ${records.length} records`);
    
    // Get schema to get schemaId
    const schemas = await getTableSchemas(appPrefix);
    const schema = schemas.find(s => s.tableName === tableName);
    if (!schema) {
      throw new Error(`Schema not found for table: ${tableName}`);
    }
    
    const result = await apiRequest('/data-records/bulk-create', {
      method: 'POST',
      body: JSON.stringify({
        appId: appPrefix,
        appPrefix: appPrefix,
        schemaId: schema.id,
        userId: userId,
        records: records
      })
    });
    
    console.log(`âœ… [DataManagement] Bulk create completed: ${records.length} records`);
    return { success: true, count: result.createdCount || records.length };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to bulk create records:', error);
    throw error;
  }
};

// ============================================
// CSV OPERATIONS
// ============================================

/**
 * Import CSV - Create new table with auto-detection
 * @param {File} file - CSV file
 * @param {string} appPrefix - App identifier
 * @param {string} userId - Current user ID
 * @param {string} displayName - Table display name
 */
export const importCSVCreateTable = async (file, appPrefix, userId, displayName) => {
  try {
    console.log(`ðŸ“¤ [DataManagement] Importing CSV to create new table: ${displayName}`);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('appId', appPrefix);
    formData.append('appPrefix', appPrefix);
    formData.append('userId', userId);
    formData.append('createNewTable', 'true');
    formData.append('displayName', displayName);
    
    const result = await apiRequest('/data-records/import-csv', {
      method: 'POST',
      body: formData
    });
    
    console.log(`âœ… [DataManagement] CSV imported successfully:`, result);
    
    // Normalize the response structure
    return {
      success: true,
      displayName: result.displayName || result.schema?.displayName || displayName,
      schemaId: result.schemaId || result.schema?.schemaId,
      recordsCreated: result.recordsCreated || result.recordCount || result.insertedCount || 0,
      fieldsDetected: result.fieldsDetected || result.fields || []
    };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to import CSV:', error);
    throw error;
  }
};

/**
 * Import CSV - Append to existing table
 * @param {File} file - CSV file
 * @param {string} appPrefix - App identifier
 * @param {string} userId - Current user ID
 * @param {string} schemaId - Existing schema ID
 */
export const importCSVAppend = async (file, appPrefix, userId, schemaId) => {
  try {
    console.log(`ðŸ“¤ [DataManagement] Appending CSV to existing table: ${schemaId}`);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('appId', appPrefix);
    formData.append('appPrefix', appPrefix);
    formData.append('userId', userId);
    formData.append('createNewTable', 'false');
    formData.append('schemaId', schemaId);
    
    const result = await apiRequest('/data-records/import-csv', {
      method: 'POST',
      body: formData
    });
    
    console.log(`âœ… [DataManagement] CSV appended successfully`);
    return result;
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to append CSV:', error);
    throw error;
  }
};

/**
 * Export records to CSV via API
 * @param {string} appPrefix - App identifier
 * @param {string} schemaId - Schema ID
 * @param {Array} recordIds - Optional array of specific record IDs
 * @param {boolean} includeSystemFields - Include system fields in export
 */
export const exportCSVAPI = async (appPrefix, schemaId, recordIds = null, includeSystemFields = false) => {
  try {
    console.log(`ðŸ“¥ [DataManagement] Exporting CSV for schema: ${schemaId}`);
    
    const params = new URLSearchParams({
      appId: appPrefix,
      appPrefix: appPrefix,
      schemaId: schemaId,
      includeSystemFields: includeSystemFields.toString()
    });
    
    if (recordIds && recordIds.length > 0) {
      params.append('recordIds', recordIds.join(','));
    }
    
    const url = `${API_BASE_URL}/data-records/export-csv?${params.toString()}`;
    
    // Download file directly
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }
    
    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition 
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `export_${Date.now()}.csv`;
    
    // Create download link
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
    
    console.log(`âœ… [DataManagement] CSV exported: ${filename}`);
    return { success: true, filename };
  } catch (error) {
    console.error('âŒ [DataManagement] Failed to export CSV:', error);
    throw error;
  }
};

/**
 * Parse CSV file and detect field types (client-side, for preview)
 * @param {File} file - CSV file
 * @returns {Promise} Parsed data with detected types
 */
export const parseCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false, // We'll handle type detection manually
      complete: (results) => {
        if (results.errors.length > 0) {
          console.warn('âš ï¸ [DataManagement] CSV parsing warnings:', results.errors);
        }
        
        const { data, meta } = results;
        
        // Detect field types from data
        const fields = meta.fields.map(fieldName => ({
          name: fieldName,
          type: detectFieldType(data, fieldName),
          required: false,
          options: []
        }));
        
        console.log(`âœ… [DataManagement] CSV parsed: ${data.length} rows, ${fields.length} fields`);
        resolve({ data, fields, headers: meta.fields });
      },
      error: (error) => {
        console.error('âŒ [DataManagement] CSV parsing failed:', error);
        reject(error);
      }
    });
  });
};

/**
 * Detect field type from sample data
 * @param {Array} data - Array of records
 * @param {string} fieldName - Field name
 * @returns {string} Detected type
 */
const detectFieldType = (data, fieldName) => {
  // Sample first 10 non-empty values
  const samples = data
    .slice(0, 10)
    .map(row => row[fieldName])
    .filter(val => val !== null && val !== undefined && val !== '');
  
  if (samples.length === 0) return 'text';
  
  // Check if all samples are emails
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const allEmails = samples.every(val => emailRegex.test(val));
  if (allEmails) return 'email';
  
  // Check if all samples are phone numbers (10 digits)
  const phoneRegex = /^\d{10}$/;
  const allPhones = samples.every(val => phoneRegex.test(String(val).replace(/\D/g, '')));
  if (allPhones) return 'phone';
  
  // Check if all samples are numbers
  const allNumbers = samples.every(val => !isNaN(val) && val.trim() !== '');
  if (allNumbers) return 'number';
  
  // Check if all samples are booleans
  const allBooleans = samples.every(val => 
    ['true', 'false', '1', '0', 'yes', 'no'].includes(String(val).toLowerCase())
  );
  if (allBooleans) return 'boolean';
  
  // Check if all samples are dates
  const allDates = samples.every(val => {
    const parsed = Date.parse(val);
    return !isNaN(parsed) && parsed > 0;
  });
  if (allDates) return 'date';
  
  // Check if limited unique values (potential select field)
  const uniqueValues = [...new Set(samples)];
  if (uniqueValues.length <= 10 && uniqueValues.length < samples.length / 2) {
    return 'select';
  }
  
  // Default to text
  return 'text';
};

/**
 * Convert CSV data to records based on schema
 * @param {Array} csvData - Parsed CSV data
 * @param {Array} fields - Field definitions
 * @returns {Array} Converted records
 */
export const convertCSVToRecords = (csvData, fields) => {
  return csvData.map(row => {
    const record = {};
    
    fields.forEach(field => {
      let value = row[field.name];
      
      // Type conversion
      switch (field.type) {
        case 'number':
          record[field.name] = value ? parseFloat(value) : null;
          break;
        case 'boolean':
          record[field.name] = ['true', '1', 'yes'].includes(String(value).toLowerCase());
          break;
        case 'date':
        case 'datetime':
          record[field.name] = value ? new Date(value).toISOString() : null;
          break;
        default:
          record[field.name] = value || '';
      }
    });
    
    return record;
  });
};

/**
 * Export records to CSV (client-side)
 * @param {Array} records - Array of records
 * @param {Array} fields - Field definitions (optional, uses all fields if not provided)
 * @returns {string} CSV string
 */
export const exportToCSV = (records, fields = null) => {
  if (records.length === 0) {
    return '';
  }
  
  // Determine fields to export
  let exportFields = fields;
  if (!exportFields) {
    // Get all unique field names from records (excluding system fields)
    const systemFields = ['createdBy', 'createdAt', 'updatedBy', 'updatedAt'];
    const allFields = new Set();
    records.forEach(record => {
      Object.keys(record).forEach(key => {
        if (!systemFields.includes(key) && key !== 'id') {
          allFields.add(key);
        }
      });
    });
    exportFields = Array.from(allFields).map(name => ({ name }));
  }
  
  const csv = Papa.unparse({
    fields: exportFields.map(f => f.name),
    data: records.map(record => {
      const row = {};
      exportFields.forEach(field => {
        row[field.name] = record[field.name] || '';
      });
      return row;
    })
  });
  
  console.log(`âœ… [DataManagement] Exported ${records.length} records to CSV`);
  return csv;
};

/**
 * Download CSV file (client-side)
 * @param {string} csvContent - CSV content
 * @param {string} filename - Filename
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  console.log(`âœ… [DataManagement] Downloaded CSV: ${filename}`);
};

// ============================================
// VALIDATION UTILITIES
// ============================================

/**
 * Validate record data against schema (client-side validation)
 * @param {Object} record - Record data
 * @param {Array} fields - Field definitions
 * @returns {Object} Validation result {valid, errors}
 */
export const validateRecord = (record, fields) => {
  const errors = {};
  
  fields.forEach(field => {
    const value = record[field.name];
    
    // Check required fields
    if (field.required && (value === null || value === undefined || value === '')) {
      errors[field.name] = `${field.name} is required`;
      return;
    }
    
    // Skip validation if value is empty and not required
    if (!value && !field.required) return;
    
    // Type-specific validation
    switch (field.type) {
      case 'number':
      case 'currency':
        if (isNaN(value)) {
          errors[field.name] = `${field.name} must be a number`;
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[field.name] = `${field.name} must be a valid email`;
        }
        break;
      case 'phone':
        const phoneDigits = String(value).replace(/\D/g, '');
        if (phoneDigits.length !== 10) {
          errors[field.name] = `${field.name} must be a 10-digit phone number`;
        }
        break;
      case 'url':
        try {
          new URL(value);
        } catch {
          errors[field.name] = `${field.name} must be a valid URL`;
        }
        break;
      case 'date':
      case 'datetime':
        if (isNaN(Date.parse(value))) {
          errors[field.name] = `${field.name} must be a valid date`;
        }
        break;
      case 'percentage':
        const percentNum = parseFloat(value);
        if (isNaN(percentNum) || percentNum < 0 || percentNum > 100) {
          errors[field.name] = `${field.name} must be between 0 and 100`;
        }
        break;
      case 'rating':
        const ratingNum = parseFloat(value);
        if (isNaN(ratingNum) || ratingNum < 1 || ratingNum > 5) {
          errors[field.name] = `${field.name} must be between 1 and 5`;
        }
        break;
      case 'color':
        const colorRegex = /^#[0-9A-Fa-f]{6}$/;
        if (!colorRegex.test(value)) {
          errors[field.name] = `${field.name} must be a valid hex color (e.g., #FF5733)`;
        }
        break;
      case 'select':
        if (field.options && field.options.length > 0 && !field.options.includes(value)) {
          errors[field.name] = `${field.name} must be one of: ${field.options.join(', ')}`;
        }
        break;
      case 'multiselect':
        if (field.options && field.options.length > 0 && Array.isArray(value)) {
          const invalidValues = value.filter(v => !field.options.includes(v));
          if (invalidValues.length > 0) {
            errors[field.name] = `Invalid options: ${invalidValues.join(', ')}`;
          }
        }
        break;
    }
  });
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate record via API
 * @param {string} appPrefix - App identifier
 * @param {string} schemaId - Schema ID
 * @param {Object} data - Record data to validate
 */
export const validateRecordAPI = async (appPrefix, schemaId, data) => {
  try {
    const result = await apiRequest('/data-records/validate', {
      method: 'POST',
      body: JSON.stringify({
        appId: appPrefix,
        appPrefix: appPrefix,
        schemaId: schemaId,
        data: data
      })
    });
    
    return {
      valid: result.valid || false,
      errors: result.errors || {}
    };
  } catch (error) {
    console.error('âŒ [DataManagement] Validation failed:', error);
    return {
      valid: false,
      errors: { _general: error.message }
    };
  }
};

export default {
  // Schema operations
  createTableSchema,
  getTableSchemas,
  getTableSchema,
  deleteTableSchema,
  
  // Record operations
  createRecord,
  getRecords,
  getRecordCount,
  updateRecord,
  deleteRecord,
  bulkDeleteRecords,
  bulkCreateRecords,
  
  // CSV operations
  parseCSVFile,
  convertCSVToRecords,
  exportToCSV,
  downloadCSV,
  importCSVCreateTable,
  importCSVAppend,
  exportCSVAPI,
  
  // Validation
  validateRecord,
  validateRecordAPI
};



